import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ordersAPI } from '../api/vendly';
import styles from './OrdersPage.module.css';

const formatPrice = (n) => `₦${Number(n).toLocaleString()}`;

const STATUS_COLORS = {
  pending_payment:      { bg: '#FFF3CD', color: '#856404', dark_bg: '#3A2E00', dark_color: '#FFD54F' },
  pending_confirmation: { bg: '#E3F2FD', color: '#0D47A1', dark_bg: '#0A1E3A', dark_color: '#64B5F6' },
  waiting_to_ship:      { bg: '#E8F5E9', color: '#1B5E20', dark_bg: '#0A2A0F', dark_color: '#69F0AE' },
  shipped:              { bg: '#E8F4FD', color: '#0D47A1', dark_bg: '#0A1E3A', dark_color: '#64B5F6' },
  delivered:            { bg: '#E8F5E9', color: '#1B5E20', dark_bg: '#0A2A0F', dark_color: '#69F0AE' },
  cancelled:            { bg: '#FFEBEE', color: '#B71C1C', dark_bg: '#2A0A0A', dark_color: '#FF5252' },
};

const STATUS_ICONS = {
  pending_payment:      '💳',
  pending_confirmation: '🔍',
  waiting_to_ship:      '📦',
  shipped:              '🚚',
  delivered:            '🎉',
  cancelled:            '❌',
};

const STATUS_LABELS = {
  pending_payment:      'Pending payment',
  pending_confirmation: 'Verifying payment',
  waiting_to_ship:      'Waiting to be shipped',
  shipped:              'Shipped',
  delivered:            'Delivered',
  cancelled:            'Cancelled',
};

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    ordersAPI.list()
      .then(res => setOrders(res.data.orders))
      .catch(() => setError('Failed to load orders. Make sure the backend is running.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className={styles.page}>
      <div className="container">
        <h1 className={styles.title}>My orders</h1>
        <div className={styles.skeletons}>
          {[1,2,3].map(i => <div key={i} className={styles.skeleton} />)}
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      <div className="container">
        <div className={styles.header}>
          <h1 className={styles.title}>My orders</h1>
          <Link to="/shop" className={styles.shopBtn}>Continue shopping →</Link>
        </div>

        {error && <div className={styles.errorBanner}>{error}</div>}

        {!error && orders.length === 0 ? (
          <div className={styles.empty}>
            <span>📦</span>
            <h2>No orders yet</h2>
            <p>When you place an order it will show up here</p>
            <Link to="/shop" className={styles.ctaBtn}>Start shopping</Link>
          </div>
        ) : (
          <div className={styles.list}>
            {orders.map(order => (
              <div key={order.id} className={styles.orderCard}>
                {/* Order header */}
                <div className={styles.orderHead} onClick={() => setExpanded(expanded === order.id ? null : order.id)}>
                  <div className={styles.orderMeta}>
                    <span className={styles.orderId}>Order #{order.id}</span>
                    <span className={styles.orderDate}>
                      {new Date(order.createdAt).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                  <div className={styles.orderRight}>
                    <span className={styles.orderTotal}>{formatPrice(order.total)}</span>
                    <span className={styles.statusBadge} style={{ background: STATUS_COLORS[order.status]?.bg, color: STATUS_COLORS[order.status]?.color }}>
                      {STATUS_ICONS[order.status]} {STATUS_LABELS[order.status] || order.status}
                    </span>
                    <Link
                      to={`/orders/${order.id}/track`}
                      className={styles.trackBtn}
                      onClick={e => e.stopPropagation()}
                    >
                      Track
                    </Link>
                    <svg className={`${styles.chevron} ${expanded === order.id ? styles.chevronOpen : ''}`}
                      width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </div>
                </div>

                {/* Order items (expanded) */}
                <div className={`${styles.orderBody} ${expanded === order.id ? styles.orderBodyOpen : ''}`}>
                  <div className={styles.orderBodyInner}>
                    <div className={styles.itemsList}>
                      {order.items.map(item => (
                        <div key={item.id} className={styles.orderItem}>
                          <div className={styles.orderItemInfo}>
                            <p className={styles.orderItemName}>{item.productName}</p>
                            <p className={styles.orderItemMeta}>Qty: {item.quantity} × {formatPrice(item.unitPrice)}</p>
                          </div>
                          <span className={styles.orderItemTotal}>{formatPrice(item.subtotal)}</span>
                        </div>
                      ))}
                    </div>
                    <div className={styles.orderSummary}>
                      <div className={styles.summaryRow}><span>Delivery</span><span>{order.deliveryFee === 0 ? 'FREE' : formatPrice(order.deliveryFee)}</span></div>
                      <div className={`${styles.summaryRow} ${styles.summaryTotal}`}><span>Total paid</span><span>{formatPrice(order.total)}</span></div>
                    </div>
                    {order.address?.city && (
                      <div className={styles.deliveryInfo}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                        </svg>
                        {order.address.line}, {order.address.city}, {order.address.state}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
